/**
 * Reports Engine Hook
 * 
 * Centralized hook for fetching and calculating report data.
 * Uses existing data fetching patterns and the reports-engine for calculations.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useProfile } from './useProfile';
import { 
  format,
  subMonths,
  startOfMonth,
  endOfMonth,
  differenceInDays,
  eachMonthOfInterval,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ReportFilters,
  calculateFinancialSummary,
  calculateProjectReports,
  calculateClientReports,
  calculateTimeReport,
  calculatePredictiveAnalysis,
  calculateMonthlyComparison,
  generateExecutiveSummary,
  type FinancialSummary,
  type ProjectReport,
  type ClientReport,
  type TimeReport,
  type PredictiveAnalysis,
  type ExecutiveSummary,
  type MonthlyComparison,
} from '@/lib/reports-engine';

// ============= Financial Report =============

export function useFinancialReport(filters: ReportFilters) {
  const { user } = useAuth();
  const { data: profile } = useProfile();

  return useQuery({
    queryKey: ['reports', 'financial', user?.id, filters.startDate, filters.endDate, filters.clientIds, filters.projectIds],
    queryFn: async (): Promise<FinancialSummary | null> => {
      if (!user?.id) return null;

      const startStr = format(filters.startDate, 'yyyy-MM-dd');
      const endStr = format(filters.endDate, 'yyyy-MM-dd');

      // Fetch payments
      let paymentsQuery = supabase
        .from('payments')
        .select('amount, status, project_id')
        .eq('user_id', user.id);

      // Filter by paid_date for confirmed, due_date for pending
      const { data: paidPayments } = await paymentsQuery
        .eq('status', 'paid')
        .gte('paid_date', startStr)
        .lte('paid_date', endStr);

      const { data: pendingPayments } = await supabase
        .from('payments')
        .select('amount, status, project_id')
        .eq('user_id', user.id)
        .in('status', ['pending', 'overdue'])
        .gte('due_date', startStr)
        .lte('due_date', endStr);

      const allPayments = [...(paidPayments || []), ...(pendingPayments || [])];

      // Fetch time entries
      const { data: timeEntries } = await supabase
        .from('time_entries')
        .select('duration_minutes, is_billable, hourly_rate, project_id')
        .eq('user_id', user.id)
        .gte('start_time', filters.startDate.toISOString())
        .lte('start_time', filters.endDate.toISOString());

      const targetRate = profile?.hourly_rate || 80;

      return calculateFinancialSummary(
        allPayments || [],
        timeEntries || [],
        targetRate
      );
    },
    enabled: !!user?.id,
  });
}

// ============= Project Reports =============

export function useProjectReports(filters: ReportFilters) {
  const { user } = useAuth();
  const { data: profile } = useProfile();

  return useQuery({
    queryKey: ['reports', 'projects', user?.id, filters.startDate, filters.endDate],
    queryFn: async (): Promise<ProjectReport[] | null> => {
      if (!user?.id) return null;

      // Fetch projects with client names
      const { data: projects } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          billing_type,
          status,
          fixed_price,
          hourly_rate,
          estimated_hours,
          clients(name)
        `)
        .eq('user_id', user.id);

      // Fetch time entries grouped by project
      const { data: timeEntries } = await supabase
        .from('time_entries')
        .select('project_id, duration_minutes')
        .eq('user_id', user.id)
        .gte('start_time', filters.startDate.toISOString())
        .lte('start_time', filters.endDate.toISOString());

      // Fetch payments grouped by project
      const startStr = format(filters.startDate, 'yyyy-MM-dd');
      const endStr = format(filters.endDate, 'yyyy-MM-dd');
      
      const { data: payments } = await supabase
        .from('payments')
        .select('project_id, amount')
        .eq('user_id', user.id)
        .eq('status', 'paid')
        .gte('paid_date', startStr)
        .lte('paid_date', endStr);

      // Aggregate data
      const timeByProject: Record<string, number> = {};
      (timeEntries || []).forEach(e => {
        if (e.project_id) {
          timeByProject[e.project_id] = (timeByProject[e.project_id] || 0) + ((e.duration_minutes || 0) / 60);
        }
      });

      const paymentsByProject: Record<string, number> = {};
      (payments || []).forEach(p => {
        if (p.project_id) {
          paymentsByProject[p.project_id] = (paymentsByProject[p.project_id] || 0) + (Number(p.amount) || 0);
        }
      });

      const targetRate = profile?.hourly_rate || 80;

      return calculateProjectReports(
        (projects || []).map((p: any) => ({
          ...p,
          client_name: p.clients?.name,
        })),
        timeByProject,
        paymentsByProject,
        targetRate
      );
    },
    enabled: !!user?.id,
  });
}

// ============= Client Reports =============

export function useClientReports(filters: ReportFilters) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['reports', 'clients', user?.id, filters.startDate, filters.endDate],
    queryFn: async (): Promise<ClientReport[] | null> => {
      if (!user?.id) return null;

      // Fetch clients
      const { data: clients } = await supabase
        .from('clients')
        .select('id, name')
        .eq('user_id', user.id);

      // Fetch projects with client mapping
      const { data: projects } = await supabase
        .from('projects')
        .select('id, client_id')
        .eq('user_id', user.id);

      // Group projects by client
      const projectsByClient: Record<string, Array<{ id: string }>> = {};
      (projects || []).forEach(p => {
        if (p.client_id) {
          if (!projectsByClient[p.client_id]) projectsByClient[p.client_id] = [];
          projectsByClient[p.client_id].push({ id: p.id });
        }
      });

      // Fetch time entries
      const { data: timeEntries } = await supabase
        .from('time_entries')
        .select(`
          duration_minutes,
          projects!inner(client_id)
        `)
        .eq('user_id', user.id)
        .gte('start_time', filters.startDate.toISOString())
        .lte('start_time', filters.endDate.toISOString());

      // Aggregate hours by client
      const hoursByClient: Record<string, number> = {};
      (timeEntries || []).forEach((e: any) => {
        const clientId = e.projects?.client_id;
        if (clientId) {
          hoursByClient[clientId] = (hoursByClient[clientId] || 0) + ((e.duration_minutes || 0) / 60);
        }
      });

      // Fetch payments (include project_id to resolve client via project)
      const startStr = format(filters.startDate, 'yyyy-MM-dd');
      const endStr = format(filters.endDate, 'yyyy-MM-dd');
      
      const { data: payments } = await supabase
        .from('payments')
        .select('amount, client_id, project_id')
        .eq('user_id', user.id)
        .eq('status', 'paid')
        .gte('paid_date', startStr)
        .lte('paid_date', endStr);

      // Build project→client map for resolving client_id via project
      const projectToClient: Record<string, string> = {};
      (projects || []).forEach(p => {
        if (p.client_id) projectToClient[p.id] = p.client_id;
      });

      // Aggregate revenue by client (resolve via project if client_id is null)
      const revenueByClient: Record<string, number> = {};
      let totalRevenue = 0;
      (payments || []).forEach(p => {
        const amount = Number(p.amount) || 0;
        totalRevenue += amount;
        const resolvedClientId = p.client_id || (p.project_id ? projectToClient[p.project_id] : null);
        if (resolvedClientId) {
          revenueByClient[resolvedClientId] = (revenueByClient[resolvedClientId] || 0) + amount;
        }
      });

      return calculateClientReports(
        clients || [],
        projectsByClient,
        hoursByClient,
        revenueByClient,
        totalRevenue
      );
    },
    enabled: !!user?.id,
  });
}

// ============= Time Report =============

export function useTimeReport(filters: ReportFilters) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['reports', 'time', user?.id, filters.startDate, filters.endDate, filters.categories],
    queryFn: async (): Promise<TimeReport | null> => {
      if (!user?.id) return null;

      let query = supabase
        .from('time_entries')
        .select('duration_minutes, is_billable, category, start_time')
        .eq('user_id', user.id)
        .gte('start_time', filters.startDate.toISOString())
        .lte('start_time', filters.endDate.toISOString());

      if (filters.categories?.length) {
        query = query.in('category', filters.categories as any);
      }

      const { data: timeEntries } = await query;
      const daysInPeriod = differenceInDays(filters.endDate, filters.startDate) + 1;

      return calculateTimeReport(timeEntries || [], daysInPeriod);
    },
    enabled: !!user?.id,
  });
}

// ============= Predictive Analysis =============

export function usePredictiveAnalysis() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['reports', 'predictive', user?.id],
    queryFn: async (): Promise<PredictiveAnalysis | null> => {
      if (!user?.id) return null;

      const now = new Date();
      const months = eachMonthOfInterval({
        start: subMonths(now, 5),
        end: now,
      });

      const monthlyRevenues = await Promise.all(
        months.map(async (month) => {
          const monthStart = startOfMonth(month);
          const monthEnd = endOfMonth(month);
          const startStr = format(monthStart, 'yyyy-MM-dd');
          const endStr = format(monthEnd, 'yyyy-MM-dd');

          const { data: payments } = await supabase
            .from('payments')
            .select('amount')
            .eq('user_id', user.id)
            .eq('status', 'paid')
            .gte('paid_date', startStr)
            .lte('paid_date', endStr);

          const revenue = (payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
          return { month, revenue };
        })
      );

      return calculatePredictiveAnalysis(monthlyRevenues);
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
}

// ============= Monthly Comparison =============

export function useMonthlyComparisonReport() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['reports', 'monthly-comparison', user?.id],
    queryFn: async (): Promise<MonthlyComparison | null> => {
      if (!user?.id) return null;

      const now = new Date();
      const currentMonthStart = startOfMonth(now);
      const currentMonthEnd = endOfMonth(now);
      const previousMonthStart = startOfMonth(subMonths(now, 1));
      const previousMonthEnd = endOfMonth(subMonths(now, 1));

      // Current month data
      const { data: currentPayments } = await supabase
        .from('payments')
        .select('amount')
        .eq('user_id', user.id)
        .eq('status', 'paid')
        .gte('paid_date', format(currentMonthStart, 'yyyy-MM-dd'))
        .lte('paid_date', format(currentMonthEnd, 'yyyy-MM-dd'));

      const { data: currentEntries } = await supabase
        .from('time_entries')
        .select('duration_minutes')
        .eq('user_id', user.id)
        .gte('start_time', currentMonthStart.toISOString())
        .lte('start_time', currentMonthEnd.toISOString());

      const { data: currentProjects } = await supabase
        .from('projects')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'done')
        .gte('updated_at', currentMonthStart.toISOString())
        .lte('updated_at', currentMonthEnd.toISOString());

      // Previous month data
      const { data: previousPayments } = await supabase
        .from('payments')
        .select('amount')
        .eq('user_id', user.id)
        .eq('status', 'paid')
        .gte('paid_date', format(previousMonthStart, 'yyyy-MM-dd'))
        .lte('paid_date', format(previousMonthEnd, 'yyyy-MM-dd'));

      const { data: previousEntries } = await supabase
        .from('time_entries')
        .select('duration_minutes')
        .eq('user_id', user.id)
        .gte('start_time', previousMonthStart.toISOString())
        .lte('start_time', previousMonthEnd.toISOString());

      const { data: previousProjects } = await supabase
        .from('projects')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'done')
        .gte('updated_at', previousMonthStart.toISOString())
        .lte('updated_at', previousMonthEnd.toISOString());

      return calculateMonthlyComparison(
        {
          revenue: (currentPayments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
          hours: (currentEntries || []).reduce((sum, e) => sum + ((e.duration_minutes || 0) / 60), 0),
          projects: currentProjects?.length || 0,
        },
        {
          revenue: (previousPayments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
          hours: (previousEntries || []).reduce((sum, e) => sum + ((e.duration_minutes || 0) / 60), 0),
          projects: previousProjects?.length || 0,
        }
      );
    },
    enabled: !!user?.id,
  });
}

// ============= Executive Summary =============

export function useExecutiveSummary() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { data: comparison } = useMonthlyComparisonReport();
  const { data: predictive } = usePredictiveAnalysis();

  return useQuery({
    queryKey: ['reports', 'executive', user?.id, comparison, predictive],
    queryFn: async (): Promise<ExecutiveSummary | null> => {
      if (!user?.id || !comparison || !predictive) return null;

      const targetRate = profile?.hourly_rate || 80;

      // Get client revenues for current month
      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      const startStr = format(monthStart, 'yyyy-MM-dd');
      const endStr = format(monthEnd, 'yyyy-MM-dd');

      const { data: clientPayments } = await supabase
        .from('payments')
        .select(`
          amount,
          clients(id, name)
        `)
        .eq('user_id', user.id)
        .eq('status', 'paid')
        .gte('paid_date', startStr)
        .lte('paid_date', endStr);

      // Aggregate by client
      const clientRevenues: Record<string, { name: string; revenue: number }> = {};
      (clientPayments || []).forEach((p: any) => {
        const clientId = p.clients?.id || 'unknown';
        const clientName = p.clients?.name || 'Sem Cliente';
        if (!clientRevenues[clientId]) {
          clientRevenues[clientId] = { name: clientName, revenue: 0 };
        }
        clientRevenues[clientId].revenue += Number(p.amount) || 0;
      });

      // Get project revenues
      const { data: projectPayments } = await supabase
        .from('payments')
        .select(`
          amount,
          projects(id, name)
        `)
        .eq('user_id', user.id)
        .eq('status', 'paid')
        .gte('paid_date', startStr)
        .lte('paid_date', endStr);

      const { data: timeEntries } = await supabase
        .from('time_entries')
        .select('project_id, duration_minutes')
        .eq('user_id', user.id)
        .gte('start_time', monthStart.toISOString())
        .lte('start_time', monthEnd.toISOString());

      // Aggregate project data
      const projectData: Record<string, { name: string; revenue: number; hours: number }> = {};
      (projectPayments || []).forEach((p: any) => {
        const projectId = p.projects?.id;
        if (projectId) {
          if (!projectData[projectId]) {
            projectData[projectId] = { name: p.projects.name, revenue: 0, hours: 0 };
          }
          projectData[projectId].revenue += Number(p.amount) || 0;
        }
      });

      (timeEntries || []).forEach(e => {
        if (e.project_id && projectData[e.project_id]) {
          projectData[e.project_id].hours += (e.duration_minutes || 0) / 60;
        }
      });

      const clients = Object.values(clientRevenues);
      const projects = Object.values(projectData).map(p => ({
        name: p.name,
        revenue: p.revenue,
        rate: p.hours > 0 ? p.revenue / p.hours : 0,
      }));

      const totalHours = (timeEntries || []).reduce((sum, e) => sum + ((e.duration_minutes || 0) / 60), 0);
      const realHourlyRate = totalHours > 0 ? comparison.currentRevenue / totalHours : 0;

      return generateExecutiveSummary(
        comparison.currentRevenue,
        comparison.previousRevenue,
        clients,
        projects,
        realHourlyRate,
        targetRate,
        predictive
      );
    },
    enabled: !!user?.id && !!comparison && !!predictive,
  });
}

// ============= Weekly Heatmap =============

export function useWeeklyHeatmap(filters: ReportFilters) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['reports', 'heatmap', user?.id, filters.startDate, filters.endDate],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: entries } = await supabase
        .from('time_entries')
        .select('start_time, duration_minutes')
        .eq('user_id', user.id)
        .gte('start_time', filters.startDate.toISOString())
        .lte('start_time', filters.endDate.toISOString());

      const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
      const hours = Array.from({ length: 24 }, (_, i) => i);

      // Initialize grid
      const grid: Record<string, Record<number, number>> = {};
      weekDays.forEach(day => {
        grid[day] = {};
        hours.forEach(hour => {
          grid[day][hour] = 0;
        });
      });

      // Fill with data
      entries?.forEach(entry => {
        const date = new Date(entry.start_time);
        const dayIndex = date.getDay();
        const hour = date.getHours();
        grid[weekDays[dayIndex]][hour] += (entry.duration_minutes || 0) / 60;
      });

      // Convert to array format
      const result: Array<{ day: string; hour: number; value: number }> = [];
      weekDays.forEach(day => {
        hours.forEach(hour => {
          result.push({
            day,
            hour,
            value: Math.round(grid[day][hour] * 10) / 10,
          });
        });
      });

      return result;
    },
    enabled: !!user?.id,
  });
}

// ============= Revenue by Month =============

export function useRevenueByMonth(monthsBack = 12) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['reports', 'revenue-by-month', user?.id, monthsBack],
    queryFn: async () => {
      if (!user?.id) return null;

      const now = new Date();
      const months = eachMonthOfInterval({
        start: subMonths(now, monthsBack - 1),
        end: now,
      });

      const result = await Promise.all(months.map(async (month) => {
        const monthStart = startOfMonth(month);
        const monthEnd = endOfMonth(month);
        const startStr = format(monthStart, 'yyyy-MM-dd');
        const endStr = format(monthEnd, 'yyyy-MM-dd');

        const { data: payments } = await supabase
          .from('payments')
          .select(`
            amount,
            projects (billing_type)
          `)
          .eq('user_id', user.id)
          .eq('status', 'paid')
          .gte('paid_date', startStr)
          .lte('paid_date', endStr);

        let hourlyRevenue = 0;
        let fixedRevenue = 0;
        
        payments?.forEach((payment: any) => {
          const amount = Number(payment.amount) || 0;
          if (payment.projects?.billing_type === 'hourly') {
            hourlyRevenue += amount;
          } else {
            fixedRevenue += amount;
          }
        });

        return {
          month: format(month, 'MMM', { locale: ptBR }),
          fullMonth: format(month, 'MMMM yyyy', { locale: ptBR }),
          hourly: Math.round(hourlyRevenue * 100) / 100,
          fixed: Math.round(fixedRevenue * 100) / 100,
          total: Math.round((hourlyRevenue + fixedRevenue) * 100) / 100,
        };
      }));

      return result;
    },
    enabled: !!user?.id,
  });
}
